import FundingOption from "constants/FundingOption";
import {
  ApplePay,
  BankAccount,
  Card,
  Cash,
  CreditCard,
  CreditCardAmex,
  Debit,
  GooglePay,
  PayPal,
  PinlessDebit,
} from "constants/FundingSourceType";
import {
  BANK_ACCCOUNT_MODAL,
  COMMON_NAMESPACE,
  PAYMENT_METHOD_MODAL_NAMESPACE,
  RECENT_PAYMENTS_NAMESPACE,
} from "constants/i18n-namespaces";
import { SCHEDULED } from "constants/paymentStatus";
import { Badge, TextButton, Tooltip, TooltipEnums } from "kubra-ux-forge";
import moment from "moment";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  IFundingSource,
  IPaymentRecord,
} from "types/Api/Payment/paymentRecord";
import { IContactInfoProperties } from "types/biller";
import { ICharityState } from "types/charity";
import {
  getBadgeStyle,
  mappingPaymentStatus,
  paymentMethodLabel,
  phoneFormat,
  upperCaseFirstLetterInString,
} from "utilities/helper";
import {
  getLastTransactionAmount,
  getLastTransactionDate,
} from "./paymentTransactionHelpers";

interface PaymentContentProps {
  payment: IPaymentRecord;
  upcomingPayment: IPaymentRecord;
  isMobile: boolean;
  selectedCharities: ICharityState[];
  isUpcomingCancellable: boolean;
  contactUs?: IContactInfoProperties;
  currencyFormatter: {
    format: (amount: number) => string;
  };
  onCancelClick: () => void;
  isExpanded?: boolean;
}

export const PaymentContent = ({
  payment,
  upcomingPayment,
  isMobile,
  selectedCharities,
  isUpcomingCancellable,
  contactUs,
  currencyFormatter,
  onCancelClick,
  isExpanded,
}: PaymentContentProps) => {
  const { t } = useTranslation([
    RECENT_PAYMENTS_NAMESPACE,
    PAYMENT_METHOD_MODAL_NAMESPACE,
    BANK_ACCCOUNT_MODAL,
  ]);

  if (!payment?.latestItemTransactionStatus) {
    return <></>;
  }

  const getBankAccountTypeLabel = (methodOfPayment: string): string => {
    switch (methodOfPayment.trim().toLowerCase()) {
      case "checking":
        return t("checking", { ns: BANK_ACCCOUNT_MODAL });
      case "savings":
        return t("savings", { ns: BANK_ACCCOUNT_MODAL });
      case "business checking":
        return t("business-checking", { ns: BANK_ACCCOUNT_MODAL });
      case "business savings":
        return t("business-savings", { ns: BANK_ACCCOUNT_MODAL });
      default:
        return "";
    }
  };

  const getPaymentMethod = (fundingSource: IFundingSource): string => {
    switch (fundingSource.fundingSourceType) {
      case BankAccount:
        return (
          (fundingSource.methodOfPayment == null
            ? ""
            : getBankAccountTypeLabel(fundingSource.methodOfPayment)) +
          " *" +
          fundingSource?.bankAccountDetails?.maskedPaymentAccount
        );
      case CreditCard:
      case Debit:
      case Card:
      case PinlessDebit:
      case CreditCardAmex:
        return (
          (fundingSource.methodOfPayment ?? "") +
          " *" +
          fundingSource.cardDetails.maskedPaymentAccount
        );
      case GooglePay:
        return GooglePay;
      case ApplePay:
        return ApplePay;
      case PayPal:
        return PayPal;
      case Cash:
        return t("cash", { ns: PAYMENT_METHOD_MODAL_NAMESPACE });
      default:
        return "";
    }
  };

  const getDateLabel = (mappedStatus: string): string => {
    if (mappedStatus === "scheduled") {
      return t("scheduled-payment-date");
    }
    if (mappedStatus.includes("refund")) {
      return t("refund-date");
    }
    if (mappedStatus.includes("return")) {
      return t("returned-date");
    }
    return t("payment-date");
  };

  const getAmountLabel = (mappedStatus: string): string => {
    return mappedStatus.includes("refund")
      ? t("refund-amount")
      : t("payment-amount");
  };

  const getDisplayDate = (
    payment: IPaymentRecord,
    mappedStatus: string,
  ): string => {
    const date =
      mappedStatus.includes("refund") || mappedStatus.includes("return")
        ? getLastTransactionDate(payment)
        : payment.paymentDate;
    return moment.utc(date).format("MM/DD/YY");
  };

  const getDisplayAmount = (
    payment: IPaymentRecord,
    mappedStatus: string,
  ): number => {
    if (mappedStatus.includes("refund")) {
      return getLastTransactionAmount(payment) ?? 0;
    }
    return payment.paymentAmount;
  };

  const mappedStatus = mappingPaymentStatus(
    payment.latestItemTransactionStatus,
  );

  return (
    <div
      className="payment-receipt-container"
      style={{ padding: "0px 72px 0px 0px" }}
    >
      {mappedStatus === SCHEDULED && !isUpcomingCancellable && (
        <>
          <div className="payment-receipt-sub-title">
            <div
              dangerouslySetInnerHTML={{
                __html: t("receipt-delivery-info", {
                  phone: phoneFormat(contactUs?.phone ?? ""),
                }),
              }}
            />
          </div>
          {!!upcomingPayment?.initiator?.phoneNumber && (
            <div className="payment-receipt-sub-title">
              <div
                dangerouslySetInnerHTML={{
                  __html: t("receipt-delivery-info-email-phone", {
                    email: upcomingPayment?.initiator?.emailAddress,
                    mobileNumber: phoneFormat(
                      upcomingPayment?.initiator?.phoneNumber ?? "",
                    ),
                  }),
                }}
              />
            </div>
          )}
          {!upcomingPayment?.initiator?.phoneNumber && (
            <div className="payment-receipt-sub-title">
              <div
                dangerouslySetInnerHTML={{
                  __html: t("receipt-delivery-info-email-only", {
                    email: upcomingPayment?.initiator?.emailAddress,
                  }),
                }}
              />
            </div>
          )}
        </>
      )}

      <dl className="payment-receipt-details-container">
        <dt
          className="payment-receipt-details-left-label"
          data-cy="recent-payment-confirmation-number-label"
        >
          {t("confirmation-number")}
        </dt>
        <dd
          className="review-charges-right-label"
          data-cy="recent-payment-confirmation-number-value"
        >
          {payment.id}
        </dd>

        <dt
          className="payment-receipt-details-left-label"
          data-cy="recent-payment-status-label"
        >
          {t("status")}
        </dt>
        <dd
          className="review-charges-right-label"
          data-cy="recent-payment-status-value"
        >
          <Badge
            text={upperCaseFirstLetterInString(
              t(mappedStatus, { ns: COMMON_NAMESPACE }),
            )}
            variant={getBadgeStyle(payment.latestItemTransactionStatus)}
          />
        </dd>

        <dt
          className="payment-receipt-details-left-label"
          data-cy="recent-payment-date-label"
        >
          {getDateLabel(mappedStatus)}
        </dt>
        <dd
          className="review-charges-right-label"
          data-cy="recent-payment-date-value"
        >
          {getDisplayDate(payment, mappedStatus)}
        </dd>

        <dt
          className="payment-receipt-details-left-label"
          data-cy="recent-payment-payment-method-label"
        >
          {t("payment-method")}
        </dt>
        <dd
          className="review-charges-right-label"
          data-cy="recent-payment-payment-method-value"
        >
          {paymentMethodLabel(getPaymentMethod(payment.fundingSource))}
        </dd>

        {payment.fundingSource.fundingSourceType == FundingOption.Cash && (
          <>
            <dt
              className="payment-receipt-details-left-label"
              data-cy="recent-payment-retailer-name-label"
            >
              {t("retailer-name")}
            </dt>
            <dd
              className="review-charges-right-label"
              data-cy="recent-payment-retailer-name-value"
            >
              {payment.itemIncommContext?.retailerName}
            </dd>
          </>
        )}

        <dt
          className="payment-receipt-details-left-label"
          data-cy="recent-payment-amount-label"
        >
          {getAmountLabel(mappedStatus)}
        </dt>
        <dd
          className="review-charges-right-label"
          data-cy="recent-payment-amount-value"
        >
          {currencyFormatter.format(getDisplayAmount(payment, mappedStatus))}
        </dd>

        {payment.feeAmount > 0 && (
          <>
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-element-interactions */}
            <dt
              className="payment-receipt-left-service-fee-label-container"
              data-cy="recent-payment-service-fee-label-container"
              onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              data-testid="service-fee-label-container"
            >
              <span className="payment-receipt-details-left-label">
                {t("service-fee")}{" "}
              </span>
              {!isMobile && isExpanded && (
                <Tooltip
                  direction={TooltipEnums.Right}
                  content={`${t("service-fee-tooltip")}`}
                />
              )}
            </dt>
            <dd
              className="review-charges-right-label"
              data-cy="recent-payment-service-fee-value"
            >
              {currencyFormatter.format(payment.feeAmount)}
            </dd>
          </>
        )}

        {selectedCharities &&
          payment.fundingSource.fundingSourceType != FundingOption.Cash &&
          selectedCharities.length > 0 &&
          selectedCharities.map(
            (charityState) =>
              charityState.selected &&
              charityState.amount > 0 && (
                <React.Fragment
                  key={payment.id + "-" + charityState.charity.code}
                >
                  <dt
                    className="payment-receipt-details-left-label"
                    data-cy={`recent-payment-donations-${charityState.charity.code}-label`}
                  >
                    {t("donation-to", { name: charityState.charity.name })}
                  </dt>
                  <dd
                    className="review-charges-right-label"
                    data-cy={`recent-payment-donations-${charityState.charity.code}-value`}
                  >
                    {currencyFormatter.format(charityState.amount)}
                  </dd>
                </React.Fragment>
              ),
          )}

        <dt
          className="payment-receipt-details-left-label"
          data-cy="recent-payment-total-label"
        >
          {t("payment-total")}
        </dt>
        <dd
          className="review-charges-right-label"
          data-cy="recent-payment-total-value"
        >
          {currencyFormatter.format(payment.totalAmount)}
        </dd>
      </dl>

      <div
        className="review-charges-bottom-text"
        data-cy="recent-payment-charges-disclaimer"
      >
        {t("charges-disclaimer")}
      </div>

      {mappedStatus === SCHEDULED && isUpcomingCancellable && (
        <div className="payment-receipt-buttons-row">
          <div className="payment-receipt-button-item cancel-payment">
            <TextButton
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                onCancelClick();
              }}
              data-cy="cancel-button"
            >
              {t("cancel-payment")}
            </TextButton>
          </div>
        </div>
      )}
    </div>
  );
};
